import { useMutation } from '@apollo/client';
import { FormControl, FormLabel, Grid, MenuItem, Select } from '@mui/material';
import { EventWaitingType, ResolvedEvent } from '@thro-types/events';

import { Field, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ADD_STUDENT_MUTATION from 'gql/mutations/EventService/enrollToEvent.gql';
import EVENT_QUERY from 'gql/queries/EventService/getEvent.gql';

import { Modal } from 'app/components';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import '../styles/styles.scss';
import { UserAutocomplete } from './UserAutocomplete';
import {
  studentInitialValues,
  studentValidationEventSchema,
  studentValidationGroupSchema
} from './validation';

interface Props {
  closeHandler: Function;
  open: boolean;
  eventData: ResolvedEvent;
}

export const AddStudentModal = (props: Props) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const dispatch = useDispatch();
  const [addStudent] = useMutation(ADD_STUDENT_MUTATION, {
    context: { clientName: 'federation' },
    refetchQueries: [EVENT_QUERY]
  });

  const handleSubmit = async values => {
    if (submitting) {
      return;
    }
    if (setDisabledCancel) {
      setDisabledCancel(true);
    }
    setSubmitting(true);
    try {
      await addStudent({
        variables: {
          refId:
            props.eventData.waitingType === EventWaitingType.GROUP ||
            props.eventData.waitingType === EventWaitingType.NONE
              ? values.groupId
              : props.eventData.id,
          userId: values.userId,
          waitingListType:
            props.eventData.waitingType === 'NONE'
              ? EventWaitingType.EVENT
              : props.eventData.waitingType
        }
      });
      props.closeHandler();
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.addStudentSuccessful())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.addStudentFailed())));
    }
    setSubmitting(false);
    setDisabledCancel(false);
  };

  const steps = ['Student'];

  return (
    <Modal
      open={props.open}
      headline={t(translations.pages.events.addParticipant())}
      onClose={(_event, reason) => {
        if (reason && reason === 'backdropClick') return;
        props.closeHandler();
      }}
      onCancel={() => props.closeHandler()}
      disabledCancel={disabledCancel}
    >
      <Grid item px={3}>
        <Formik
          validationSchema={yup
            .object()
            .shape(
              props.eventData.waitingType !== EventWaitingType.EVENT
                ? studentValidationGroupSchema(t)
                : studentValidationEventSchema(t)
            )}
          onSubmit={values => {
            handleSubmit(values);
          }}
          initialValues={studentInitialValues()}
        >
          {({ values, touched, errors, handleChange, handleBlur }) => {
            return (
              <MultistepForm
                submitting={submitting}
                steps={steps}
                hideStepper={true}
                onCancel={props.closeHandler}
              >
                {() => (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Grid item>
                        <FormControl>
                          <FormLabel>{'E-Mail'}</FormLabel>
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <UserAutocomplete eventData={props.eventData} />
                      </Grid>
                    </Grid>
                    {props.eventData.waitingType !== EventWaitingType.EVENT && (
                      <Grid item xs>
                        <Grid item>
                          <FormControl>
                            <FormLabel>
                              {t(translations.pages.events.create.groupCard.group())}
                            </FormLabel>
                          </FormControl>
                        </Grid>
                        <Grid item>
                          <Field
                            as={Select}
                            id="groupId"
                            name="groupId"
                            fullWidth
                            value={values.groupId}
                            error={touched.groupId && Boolean(errors.groupId)}
                            onBlur={handleBlur}
                            required
                            className="addStudentModal__Select"
                          >
                            {props.eventData.groups
                              ?.filter(g => !g.isCancelled)
                              .map((group, index) => {
                                return (
                                  <MenuItem value={group.id} key={group.id}>
                                    {`Gruppe ${index + 1} ${group.info ? `- ${group.info}` : ''}`}
                                  </MenuItem>
                                );
                              })}
                          </Field>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}
              </MultistepForm>
            );
          }}
        </Formik>
      </Grid>
    </Modal>
  );
};
